import React from 'react'
import { useCart } from '../../contexts/CartContext'
import { cn } from '../../utils/cn'

interface CartSummaryProps {
  className?: string
  showItemCount?: boolean
  showBreakdown?: boolean
}

export function CartSummary({ 
  className,
  showItemCount = true,
  showBreakdown = false
}: CartSummaryProps) {
  const { items, itemCount, totalAmount } = useCart()

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount)
  }

  // Calculate breakdown by item type
  const breakdown = items.reduce((acc, item) => {
    if (!acc[item.item_type]) {
      acc[item.item_type] = {
        count: 0,
        total: 0
      }
    }
    acc[item.item_type].count += item.quantity
    acc[item.item_type].total += Number(item.total_price)
    return acc
  }, {} as Record<string, { count: number; total: number }>)

  const itemTypeLabels: Record<string, string> = {
    booking: 'Bookings',
    membership: 'Memberships',
    package: 'Packages',
    custom: 'Other Items'
  }

  return (
    <div className={cn(
      "bg-[#2a2a2a] rounded-lg p-4 border border-gray-800",
      className
    )}>
      <h3 className="text-sm font-medium text-white mb-3">Cart Summary</h3>
      
      {showItemCount && (
        <div className="flex justify-between items-center text-sm mb-2">
          <span className="text-gray-400">Items</span>
          <span className="text-gray-300">{itemCount}</span>
        </div>
      )}

      {showBreakdown && Object.keys(breakdown).length > 0 && (
        <div className="space-y-2 mb-3 pt-2 border-t border-gray-700">
          {Object.entries(breakdown).map(([type, data]) => (
            <div key={type} className="flex justify-between items-center text-xs">
              <span className="text-gray-500">
                {itemTypeLabels[type] || type} ({data.count})
              </span>
              <span className="text-gray-400">
                {formatCurrency(data.total)}
              </span>
            </div>
          ))}
        </div>
      )}

      <div className="flex justify-between items-center pt-3 border-t border-gray-700">
        <span className="text-base font-medium text-white">Total</span>
        <span className="text-base font-medium text-gold">
          {formatCurrency(totalAmount)}
        </span>
      </div>

      {items.length === 0 && (
        <p className="text-xs text-gray-500 mt-3 text-center">
          Your cart is empty
        </p>
      )}
    </div>
  )
}